import '../pages.css';
import '../SenseShield.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import DateRangePicker, { computeRangeForPreset } from '../../components/Filters/DateRangePicker';
import { toUtcEndOfDay, toUtcMidnight } from '../../utils/dateFormat';
import FilterSelect from '../../components/Campaigns/FilterSelect';
import KpiRow from '../../components/SenseShield/KpiRow';
import OutcomesPerRunChart from '../../components/SenseShield/OutcomesPerRunChart';
import BugVerdictsChart from '../../components/SenseShield/BugVerdictsChart';
import BugBacklogChart from '../../components/SenseShield/BugBacklogChart';
import PromptHealthTable from '../../components/SenseShield/PromptHealthTable';

// All prompt types by default; users can filter by specific types if needed.
const DEFAULT_PROMPT_TYPES = ['vague', 'crystal clear'];

function Summary() {
  const [dateRange, setDateRange] = useState(() => ({ ...computeRangeForPreset('7d'), preset: '7d' }));
  const [promptTypeOptions, setPromptTypeOptions] = useState([]);
  const [selectedPromptTypes, setSelectedPromptTypes] = useState(DEFAULT_PROMPT_TYPES);
  const [dashboard, setDashboard] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pass / fail / warning share of all executions in the range, from the existing dashboard data.
  const statusRates = useMemo(() => {
    if (!dashboard?.statusTrend) return null;
    const totals = dashboard.statusTrend.reduce(
      (acc, item) => ({
        pass: acc.pass + (item.pass || 0),
        warning: acc.warning + (item.warning || 0),
        fail: acc.fail + (item.fail || 0),
      }),
      { pass: 0, warning: 0, fail: 0 }
    );
    const total = totals.pass + totals.warning + totals.fail;
    const pct = (n) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);
    return { pass: pct(totals.pass), fail: pct(totals.fail), warning: pct(totals.warning) };
  }, [dashboard]);

  useEffect(() => {
    api.get(`${QUALITY_SERVICE_API_URL}/hub/sense-shield/prompt-types`)
      .then((res) => setPromptTypeOptions(res.data.map((value) => ({ value }))))
      .catch(() => setPromptTypeOptions([]));
  }, []);

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set('from', toUtcMidnight(dateRange.startDate).toISOString());
    params.set('to', toUtcEndOfDay(dateRange.endDate).toISOString());
    selectedPromptTypes.forEach((promptType) => params.append('promptType', promptType));

    api.get(`${QUALITY_SERVICE_API_URL}/hub/sense-shield/dashboard?${params.toString()}`)
      .then((res) => setDashboard(res.data))
      .catch(() => setError('Failed to load Summary dashboard'))
      .finally(() => setLoading(false));
  }, [dateRange, selectedPromptTypes]);

  // Skip rate, bugs, verdicts, review coverage and per-run outcomes (see quality-service
  // hub/sense-shield-insights).
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('from', toUtcMidnight(dateRange.startDate).toISOString());
    params.set('to', toUtcEndOfDay(dateRange.endDate).toISOString());
    selectedPromptTypes.forEach((promptType) => params.append('promptType', promptType));

    api.get(`${QUALITY_SERVICE_API_URL}/hub/sense-shield/summary-insights?${params.toString()}`)
      .then((res) => setInsights(res.data))
      .catch(() => {
        setInsights(null);
        setError('Failed to load Summary insights');
      });
  }, [dateRange, selectedPromptTypes]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return (
    <div className="sense-shield-page">
      <div className="sense-shield-header">
        <h1>Summary</h1>
        <div className="sense-shield-filters">
          <FilterSelect options={promptTypeOptions} value={selectedPromptTypes} onChange={setSelectedPromptTypes} />
          <DateRangePicker
            value={{ startDate: dateRange.startDate, endDate: dateRange.endDate }}
            preset={dateRange.preset}
            onChange={setDateRange}
            maxDate={new Date()}
            enabledPresets={['today', '7d', '14d', '30d', 'custom']}
          />
          <button className="sense-shield-refresh" onClick={fetchDashboard} disabled={loading} type="button" aria-label="Refresh">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {error && <div className="sense-shield-error">{error}</div>}

      {dashboard && (
        <>
          {statusRates && <KpiRow rates={statusRates} kpis={insights?.kpis} />}
          <div className="sense-shield-grid">
            <h2 className="sense-shield-section-title">1 · Run health</h2>
            {insights && <div className="sense-shield-panel sense-shield-panel-full"><OutcomesPerRunChart data={insights.runs} range={{ start: dateRange.startDate, end: dateRange.endDate }} /></div>}


            <h2 className="sense-shield-section-title">2 · Bug flow</h2>
            {insights && <div className="sense-shield-panel sense-shield-panel-full"><BugVerdictsChart data={insights.verdictsPerDay} /></div>}

            <h2 className="sense-shield-section-title">3 · Open bug backlog</h2>
            {insights && <div className="sense-shield-panel sense-shield-panel-full"><BugBacklogChart data={insights.backlogOverTime} /></div>}

            <h2 className="sense-shield-section-title">4 · Prompt health</h2>
            {insights && <div className="sense-shield-panel sense-shield-panel-full"><PromptHealthTable data={insights.promptHealth} dateRange={dateRange} /></div>}
          </div>
        </>
      )}
    </div>
  );
}

export default Summary;
